import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Search, ArrowLeft, MapIcon, Frown, MapPinOff } from "lucide-react";
import api from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { localized } from "@/i18n";
import { CATEGORIES } from "@/constants";
import { toApiParams, getGeo } from "@/lib/filters";
import PlaceCard from "@/components/PlaceCard";
import FilterSheet from "@/components/FilterSheet";

function parseFilters(sp) {
  return {
    category: sp.get("category") || "",
    tags: sp.get("tags") ? sp.get("tags").split(",") : [],
    price_max: sp.get("price_max") ? Number(sp.get("price_max")) : 0,
    open_now: sp.get("open_now") === "true",
    best_moment: sp.get("best_moment") || "",
    sort: sp.get("sort") || "",
    q: sp.get("q") || "",
    good_first_visit: sp.get("good_first_visit") === "true",
    popular_students: sp.get("popular_students") === "true",
    ideal_evening: sp.get("ideal_evening") === "true",
    md: sp.get("md") ? Number(sp.get("md")) : 0,
  };
}

export default function Explore() {
  const { t, lang } = useApp();
  const navigate = useNavigate();
  const [sp, setSp] = useSearchParams();
  const [filters, setFilters] = useState(() => parseFilters(sp));
  const [places, setPlaces] = useState([]);
  const [loading, setLoading] = useState(true);
  const [geoDenied, setGeoDenied] = useState(false);
  const [qInput, setQInput] = useState(sp.get("q") || "");
  const title = sp.get("title") || t("all_places");

  const fetchPlaces = useCallback(async (f) => {
    setLoading(true);
    setGeoDenied(false);
    let geo = null;
    if (f.sort === "distance") {
      geo = await getGeo();
      if (!geo) setGeoDenied(true);
    }
    try {
      const params = toApiParams(f, geo);
      if (!geo && f.sort === "distance") delete params.sort;
      const { data } = await api.get("/places", { params });
      let result = data;
      if (f.md) result = result.filter((p) => !p.duration_min || p.duration_min <= f.md);
      setPlaces(result);
    } catch {
      setPlaces([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchPlaces(filters); /* eslint-disable-next-line */ }, []);

  const applyFilters = (f) => {
    setFilters(f);
    const next = new URLSearchParams(sp);
    ["category", "price_max", "open_now", "best_moment", "sort"].forEach((k) => next.delete(k));
    next.delete("tags");
    if (f.category) next.set("category", f.category);
    if (f.tags?.length) next.set("tags", f.tags.join(","));
    if (f.price_max) next.set("price_max", String(f.price_max));
    if (f.open_now) next.set("open_now", "true");
    if (f.best_moment) next.set("best_moment", f.best_moment);
    if (f.sort) next.set("sort", f.sort);
    setSp(next, { replace: true });
    fetchPlaces(f);
  };

  const submitSearch = (e) => {
    e.preventDefault();
    const f = { ...filters, q: qInput };
    setFilters(f);
    const next = new URLSearchParams(sp);
    next.set("q", qInput);
    next.delete("title");
    setSp(next, { replace: true });
    fetchPlaces(f);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <button data-testid="explore-back" onClick={() => navigate(-1)} className="grid h-9 w-9 place-items-center rounded-full bg-sand text-ink">
          <ArrowLeft className="h-4.5 w-4.5" />
        </button>
        <h1 className="font-heading text-xl font-extrabold text-ink truncate">{title}</h1>
      </div>

      <form onSubmit={submitSearch} className="flex items-center gap-2 rounded-2xl border border-border bg-white px-3 py-1 shadow-sm">
        <Search className="h-4.5 w-4.5 text-ink-muted" />
        <input
          data-testid="explore-search-input"
          value={qInput}
          onChange={(e) => setQInput(e.target.value)}
          placeholder={t("search_placeholder")}
          className="flex-1 bg-transparent py-2.5 text-sm outline-none"
        />
      </form>

      {/* Category chips */}
      <div className="flex gap-2 overflow-x-auto pb-1 -mx-4 px-4 scrollbar-hide">
        <button
          onClick={() => applyFilters({ ...filters, category: "" })}
          className={`shrink-0 rounded-full border px-3.5 py-1.5 text-xs font-semibold ${!filters.category ? "border-terracotta bg-terracotta text-white" : "border-border bg-white text-ink"}`}
        >{t("any")}</button>
        {CATEGORIES.map((c) => (
          <button
            key={c.id}
            data-testid={`chip-category-${c.id}`}
            onClick={() => applyFilters({ ...filters, category: filters.category === c.id ? "" : c.id })}
            className={`shrink-0 rounded-full border px-3.5 py-1.5 text-xs font-semibold ${filters.category === c.id ? "text-white" : "border-border bg-white text-ink"}`}
            style={filters.category === c.id ? { backgroundColor: c.color, borderColor: c.color } : {}}
          >{localized(c.label, lang)}</button>
        ))}
      </div>

      <div className="flex items-center justify-between">
        <FilterSheet filters={filters} onApply={applyFilters} />
        <div className="flex items-center gap-3">
          <span className="text-sm text-ink-muted">{loading ? t("loading") : `${places.length} ${places.length > 1 ? t("results") : t("result")}`}</span>
          <button data-testid="explore-to-map" onClick={() => navigate("/map")} className="inline-flex items-center gap-1.5 rounded-full bg-sea px-3.5 py-2 text-xs font-semibold text-white">
            <MapIcon className="h-4 w-4" /> {t("map_view")}
          </button>
        </div>
      </div>

      {geoDenied && (
        <div className="flex items-center gap-3 rounded-2xl border border-amber-200 bg-amber-50 p-4">
          <MapPinOff className="h-5 w-5 text-amber-600" />
          <div>
            <p className="text-sm font-semibold text-amber-800">{t("no_geo_title")}</p>
            <p className="text-xs text-amber-700">{t("no_geo_text")}</p>
          </div>
        </div>
      )}

      {loading ? (
        <div className="grid grid-cols-2 gap-4">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="aspect-[4/5] animate-pulse rounded-2xl bg-sand" />
          ))}
        </div>
      ) : places.length === 0 ? (
        <div className="flex flex-col items-center justify-center gap-3 rounded-3xl border border-dashed border-border bg-white py-16 text-center" data-testid="explore-empty">
          <Frown className="h-10 w-10 text-ink-muted" />
          <p className="font-heading font-bold text-ink">{t("no_results_title")}</p>
          <p className="max-w-xs text-sm text-ink-muted">{t("no_results_text")}</p>
          <button onClick={() => applyFilters({ ...filters, tags: [], price_max: 0, open_now: false, best_moment: "", sort: "", md: 0 })} className="mt-1 rounded-full bg-terracotta px-4 py-2 text-sm font-bold text-white">{t("reset")}</button>
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-4">
          {places.map((p) => <PlaceCard key={p.id} place={p} />)}
        </div>
      )}
    </div>
  );
}
